import BigNumber from 'bignumber.js';
import { useCallback } from 'react';
import {
  AuctionType,
  BaseRfq,
  PricingAlgoVersion,
} from '#/unified-rfqs/entities/shared';
import { Grfq, GrfqLeg } from '#/unified-rfqs/entities/grfq/domain';
import {
  Counterparty,
  Drfq,
  DrfqLeg,
} from '#/unified-rfqs/entities/drfq/domain';
import { Source } from '#/unified-rfqs/entities/products/venue';
import { findBaseProductCode, getProduct } from '#/products/domain';
import { parseInstrument } from '#/unified-rfqs/ui/rfqs/legs-functions';
import {
  useDefaultAccountsFeatureFlag,
  useUnifiedMarketsV2PricingAlgorithmFeatureFlag,
} from '@paradigm/features/src/feature-flags/context';
import { Maybe, Nullable } from '@paradigm/utils/types';
import { isCustomStrategy } from '#/unified-rfqs/ui/rfqs/rfqs-functions';
import { State } from '#/unified-rfqs/ui/rfq-builder/store';
import { DraftLeg } from '#/unified-rfqs/ui/rfq-builder/types';
import { RATIO_PRECISION } from '#/unified-rfqs/ui/ratio-precision';
import { Rfq } from '#/unified-rfqs/entities/rfq';
import { DefaultAccountsRecord } from '#/preferences/default-accounts/types';
import { useDefaultAccountsView } from '#/preferences/default-accounts/context';

export interface DrfqPayload
  extends Pick<
    Drfq,
    | 'legs'
    | 'counterparties'
    | 'role'
    | 'product_codes'
    | 'description'
    | 'type'
  > {
  readonly account_name?: string | null;
  readonly venue: string;
}

export interface GrfqPayload
  extends Pick<Grfq, 'legs' | 'product_codes' | 'description' | 'type'> {
  readonly venue: string;
}

const isDrfqPayload = (
  rfq: DrfqPayload | GrfqPayload | undefined,
): rfq is DrfqPayload => rfq?.type === 'drfq';

const isGrfq = (rfq: DrfqPayload | GrfqPayload | undefined): rfq is Grfq =>
  rfq?.type === 'grfq';

export function useMarketPayloadCreator() {
  const defaultAccounts = useDefaultAccountsView();
  const isDefaultAccountEnabled = useDefaultAccountsFeatureFlag();
  return useCallback(
    (
      rfq: DrfqPayload | GrfqPayload | undefined,
      auction: AuctionType,
      prefillAccount?: Maybe<string>,
    ) =>
      makeCreateRFQPayload(
        rfq,
        auction,
        isDefaultAccountEnabled ? defaultAccounts : null,
        prefillAccount,
      ),
    [defaultAccounts, isDefaultAccountEnabled],
  );
}

export function makeCreateRFQPayload(
  rfq: DrfqPayload | GrfqPayload | undefined,
  auction: AuctionType,
  defaultAccounts: Maybe<DefaultAccountsRecord>,
  prefillAccount?: Maybe<string>,
): Partial<State> {
  if (rfq == null) return {};

  const strategyCode = parseStrategyCode(rfq);
  const productCode = parseProductCode(rfq);
  const legs = parseStateLegs(rfq, auction);
  const venue = rfq.venue as Source;
  const shouldSelectCPs = isDrfqPayload(rfq) && auction === 'RFQ';
  const counterparties = shouldSelectCPs ? parseCounterparties(rfq) : [];
  let account =
    'account_name' in rfq && rfq.account_name != null ? rfq.account_name : null;
  if (defaultAccounts != null) {
    account = getNewRFQAccount(rfq, auction, defaultAccounts, prefillAccount);
  }

  const state: Partial<State> = {
    venue,
    account,
    auction,
    productCode,
    strategyCode,
    legs,
    counterparties,
  };

  return state;
}

export function getNewRFQAccount(
  rfq: DrfqPayload | GrfqPayload | undefined,
  auction: AuctionType,
  defaultAccounts: DefaultAccountsRecord,
  prefillAccount?: Maybe<string>,
) {
  if (auction !== 'RFQ' || rfq == null) return null;
  if (prefillAccount != null) return prefillAccount;
  // When creating from other DRFQ, return the same account
  if ('account_name' in rfq && rfq.account_name != null)
    return rfq.account_name;

  const productCode = findBaseProductCode(rfq.product_codes);
  if (productCode == null) return null;
  const { venue } = getProduct(productCode);
  return defaultAccounts[venue]?.[productCode] ?? null;
}

function parseCounterparties(rfq: DrfqPayload | Grfq): readonly Counterparty[] {
  if (
    !isDrfqPayload(rfq) ||
    rfq.role !== 'TAKER' ||
    rfq.counterparties.length === 0
  )
    return []; // LPs will be selected

  const counterparties = rfq.counterparties.map(
    (cp) => ({ desk_name: cp } as Counterparty),
  );
  return counterparties;
}

function parseProductCode(rfq: DrfqPayload | GrfqPayload) {
  if (isGrfq(rfq) || isDrfqPayload(rfq))
    return findBaseProductCode(rfq.product_codes);
  return '';
}

function parseStrategyCode(rfq: DrfqPayload | GrfqPayload): string | null {
  const [strategyCode = null] = rfq.description.split(' ');
  switch (strategyCode) {
    case 'Straddle':
      return 'Strd';
    case 'Strangle':
      return 'Strg';
    case 'Cstm':
      return null;
    default:
      return strategyCode;
  }
}

function parseStateLegs(
  rfq: DrfqPayload | GrfqPayload,
  auction: AuctionType,
): DraftLeg[] {
  const baseLegs = rfq.legs.map(
    (leg, index) => parseBaseLeg(leg, index, auction) as DraftLeg,
  );

  if (auction === 'OB') return baseLegs;

  if (isDrfqPayload(rfq)) {
    return baseLegs.map(
      (leg, index) =>
        ({
          ...leg,
          quantity: rfq.legs[index]?.quantity ?? '0',
        } as DraftLeg),
    );
  }

  return baseLegs;
}

function parseBaseLeg(
  leg: DrfqLeg | GrfqLeg,
  index: number,
  auction: AuctionType,
) {
  const product = getProduct(leg.product_code);
  const precision = RATIO_PRECISION[auction][product.kind];
  const instrumentName =
    'instrument_name' in leg ? leg.instrument_name : leg.instrument;
  const { strike, expiry, instrumentLabel } = parseInstrument(instrumentName);

  return {
    index,
    instrumentName,
    kind: product.kind,
    action: leg.side,
    ratio: BigNumber(leg.ratio).decimalPlaces(precision).toString(),
    expiration: expiry === 'Perpetual' ? null : new Date(expiry).getTime(),
    strike,
    productCode: leg.product_code,
    // The future hedge leg re-prices to the current mark via
    // useHedgeLegPricePrefill, which only fills when price is null. Carrying
    // the historical price would keep it stale and break the delta hedge.
    price: product.kind === 'FUTURE' && leg.isHedge ? null : leg.price,
    isHedge: leg.isHedge,
    ...(leg.isHedge && {
      currency: product.currency,
      marginKind: product.marginType,
    }),
    optionKind:
      instrumentLabel.toUpperCase() !== 'FUTURE'
        ? instrumentLabel.toUpperCase()
        : null,
  } as const;
}
export function useShouldEnableLegPricing(
  isLegPricingEnabled: boolean,
  rfq?: Nullable<BaseRfq>,
) {
  const isV2PricingAlgoEnabled =
    useUnifiedMarketsV2PricingAlgorithmFeatureFlag();

  return isV2PricingAlgoEnabled
    ? isLegPricingEnabled
    : isLegPricingEnabled || (rfq != null && isCustomStrategy(rfq));
}

export function usePricingAlgoVersion(
  rfq?: Nullable<BaseRfq>,
): PricingAlgoVersion {
  const isV2PricingAlgoEnabled =
    useUnifiedMarketsV2PricingAlgorithmFeatureFlag();

  return isV2PricingAlgoEnabled && rfq != null && isCustomStrategy(rfq)
    ? 'v2'
    : undefined;
}

/**
 * By switching to the new pricing algorithm, we enable leg pricing for all
 * multi-leg strategies, not just custom strategies.
 * This hook is used to determine whether the RFQ is a multi-leg strategy.
 *
 * @param rfq
 * @returns
 */
export function useIsMultiLegStrategy(rfq: Rfq) {
  const isV2PricingAlgoEnabled =
    useUnifiedMarketsV2PricingAlgorithmFeatureFlag();

  return isV2PricingAlgoEnabled ? rfq.legs.length > 1 : isCustomStrategy(rfq);
}
